import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  getFirmwareMfaStatus,
  getFirmwareById,
  updateFirmware,
  uploadFirmware,
  verifyFirmwareMfa,
} from "../../actions/doctorActions";
import { useParams } from "react-router-dom";
import "./FormComponent.css";

export default function FirmwareUploadForm() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginData.token);
  const history = useHistory();
  const { firmware } = useSelector((state) => state.firmwareDevice);

  useEffect(() => {
    if (id) {
      dispatch(getFirmwareById(token, id)).catch((error) => {
        toast.error("Error fetching this firmware");
        history.push("/firmware");
      });
    }
  }, [dispatch, token, id]);

  useEffect(() => {
    if (id || !token) {
      return;
    }

    dispatch(getFirmwareMfaStatus(token))
      .then((response) => {
        if (response?.data?.mfa_enabled) {
          setIsMfaSetupComplete(true);
        }
      })
      .catch(() => {
        // Keep setup button visible if status cannot be loaded.
      });
  }, [dispatch, token, id]);

  const defaultFormData = {
    firmware_version: "",
    changelog: "",
    directory: null,
    image: null,
    version: "",
  };

  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    if (id && firmware) {
      setFormData({
        ...defaultFormData,
        firmware_version: firmware.firmware_version || "",
        changelog: firmware.changelog || "",
        version: firmware.version || "",
      });
    }
  }, [firmware, id]);

  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState("");
  const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);
  const [pendingFirmwareData, setPendingFirmwareData] = useState(null);
  const [isMfaSetupComplete, setIsMfaSetupComplete] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({
        ...prev,
        [name]: files.length > 0 ? files[0] : null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleClear = () => {
    setFormData(defaultFormData);
  };

  const handleUploadSuccess = () => {
    toast.success("Firmware uploaded successfully!");
    setPendingFirmwareData(null);
    handleClear();
    history.replace("/firmware");
  };

  const handleUploadFailure = (error) => {
    if (error.mfaRequired) {
      setPendingFirmwareData({ ...formData });
      setMfaError("");
      setMfaCode("");
      setShowMfaModal(true);
      return;
    }
    toast.error(error.message || "Failed to upload the firmware!");
  };

  const handleCloseMfaModal = () => {
    if (isVerifyingMfa) {
      return;
    }
    setShowMfaModal(false);
    setMfaCode("");
    setMfaError("");
    setPendingFirmwareData(null);
  };

  const handleMfaVerify = async () => {
    if (!mfaCode.trim()) {
      setMfaError("Enter the 6-digit code from Google Authenticator.");
      return;
    }

    setIsVerifyingMfa(true);
    setMfaError("");

    try {
      await dispatch(verifyFirmwareMfa(mfaCode.trim(), token));
    } catch (error) {
      setMfaError(error.message || "MFA verification failed.");
      setIsVerifyingMfa(false);
      setIsSubmitting(false);
      return;
    }

    try {
      await dispatch(uploadFirmware(pendingFirmwareData || formData, token));
      setShowMfaModal(false);
      setMfaCode("");
      setMfaError("");
      handleUploadSuccess();
    } catch (error) {
      setShowMfaModal(true);
      if (error.mfaRequired) {
        setMfaError(
          error.message || "MFA verification required before firmware upload."
        );
      } else {
        setMfaError(error.message || "Failed to upload the firmware!");
        toast.error(error.message || "Failed to upload the firmware!");
      }
    } finally {
      setIsVerifyingMfa(false);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidated(true);

    const isValid = e.currentTarget.checkValidity();
    if (!isValid) return;

    if (!id && !isMfaSetupComplete) {
      toast.error(
        "Please complete Setup Authenticator on the Firmware page before uploading firmware."
      );
      return;
    }

    setIsSubmitting(true);

    if (id) {
      // For update, we don't send files
      const updateData = {
        firmware_version: formData.firmware_version,
        changelog: formData.changelog,
        version: formData.version,
      };

      dispatch(updateFirmware(updateData, token, id))
        .then((res) => {
          toast.success("Firmware updated successfully!");
          handleClear();
          history.push("/firmware");
        })
        .catch((err) => {
          toast.error(err.message || "Failed to update the firmware!");
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      // For create, we send the full form data with files
      dispatch(uploadFirmware(formData, token))
        .then(() => {
          handleUploadSuccess();
        })
        .catch((err) => {
          handleUploadFailure(err);
        })
        .finally(() => {
          if (!showMfaModal) {
            setIsSubmitting(false);
          }
        });
    }
  };

  return (
    <>
      <section className="login-main">
        <div className="container h-100">
          <div className="row justify-content-center p-0 m-0 h-100">
            <div className="col-xxl-8 col-lg-9 col-md-12 col-sm-12 py-5 h-100">
              <div className="content">
                <div className="card card-border-radius wider-card w-100 p-lg-3 p-sm-2">
                  <div className="card-title">
                    <h2 className="p-3 pb-1">
                      {id ? "Update Firmware" : "Add Firmware"}
                    </h2>
                  </div>
                  <div className="card-body">
                    <form
                      className={`needs-validation ${
                        validated ? "was-validated" : ""
                      }`}
                      noValidate
                      onSubmit={handleSubmit}
                      encType="multipart/form-data"
                    >
                      {[
                        {
                          name: "firmware_version",
                          label: "Firmware Version",
                          type: "text",
                          required: true,
                        },
                        {
                          name: "changelog",
                          label: "Changelog",
                          type: "textarea",
                          required: true,
                        },
                        {
                          name: "version",
                          label: "Version",
                          type: "text",
                          required: true,
                        },
                        ...(id
                          ? []
                          : [
                              {
                                name: "directory",
                                label: "Directory (File or Folder)",
                                type: "file",
                                required: true,
                              },
                              {
                                name: "image",
                                label: "Image (Image File)",
                                type: "file",
                                accept: "image/*",
                              },
                            ]),
                      ].map(
                        ({
                          name,
                          label,
                          type,
                          customInvalidFeedback,
                          ...rest
                        }) => (
                          <div className="mb-4" key={name}>
                            <label
                              htmlFor={name}
                              className="form-label login-form-label"
                            >
                              <strong>{label}</strong>
                            </label>
                            {type === "textarea" ? (
                              <textarea
                                className="form-control login-form-input"
                                id={name}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                {...rest}
                              />
                            ) : type === "file" ? (
                              <input
                                className="form-control login-form-input"
                                type={type}
                                id={name}
                                name={name}
                                onChange={handleChange}
                                {...rest}
                              />
                            ) : (
                              <input
                                className="form-control login-form-input"
                                type={type}
                                id={name}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                {...rest}
                              />
                            )}
                            {rest.required && (
                              <div className="invalid-feedback">
                                {customInvalidFeedback ||
                                  "This field is required."}
                              </div>
                            )}
                          </div>
                        )
                      )}

                      <div className="d-flex justify-content-start">
                        <button
                          type="submit"
                          className="btn btn-sm text-light main-bg btn-style me-2"
                          disabled={isSubmitting}
                        >
                          <b>
                            {isSubmitting
                              ? id
                                ? "Updating..."
                                : "Uploading..."
                              : id
                              ? "Update Firmware"
                              : "Upload Firmware"}
                          </b>
                        </button>
                        {!id && (
                          <button
                            type="button"
                            className="btn btn-sm text-light main-bg btn-style me-2"
                            onClick={handleClear}
                            disabled={isSubmitting}
                          >
                            <b>Clear</b>
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm text-light btn-style"
                          disabled={isSubmitting}
                          onClick={() => history.push("/firmware")}
                        >
                          <b>BACK</b>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Modal show={showMfaModal} onHide={handleCloseMfaModal} centered>
        <Modal.Header closeButton={!isVerifyingMfa}>
          <Modal.Title>Firmware Upload MFA</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Enter the 6-digit code from Google Authenticator to continue the firmware upload.
          </p>
          <input
            type="text"
            className="form-control login-form-input"
            value={mfaCode}
            maxLength={6}
            onChange={(event) => {
              const normalizedValue = event.target.value.replace(/\D/g, "");
              setMfaCode(normalizedValue);
              setMfaError("");
            }}
            placeholder="123456"
            disabled={isVerifyingMfa}
          />
          {mfaError ? <div className="text-danger mt-2">{mfaError}</div> : null}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-sm text-light btn-style"
            onClick={handleCloseMfaModal}
            disabled={isVerifyingMfa}
          >
            <b>Cancel</b>
          </button>
          <button
            type="button"
            className="btn btn-sm text-light main-bg btn-style"
            onClick={handleMfaVerify}
            disabled={isVerifyingMfa}
          >
            <b>{isVerifyingMfa ? "Verifying..." : "Verify & Upload"}</b>
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
